/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Alert,
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  FormHelperText,
  Snackbar,
  TextField,
} from '@mui/material';
import { Box } from '@mui/system';
import { Elements } from '@stripe/react-stripe-js';
import { Patient } from 'fhir/r4b';
import { FC, useEffect, useState } from 'react';
import { useGetPaymentMethods } from 'src/hooks/useGetPaymentMethods';
import { useSetDefaultPaymentMethod } from 'src/hooks/useSetDefaultPaymentMethod';
import { useSetupStripe } from 'src/hooks/useSetupStripe';
import { AddCreditCardForm } from 'ui-components/lib/components/AddCreditCardForm';
import { CreditCardBrandIcon } from 'ui-components/lib/components/CreditCardBrandIcon';
import { loadStripe } from 'ui-components/lib/utils/stripe';
import { CreditCardInfo } from 'utils/lib/types/data/payment/payment-method-types';

interface CardOption {
  id: string;
  label: string;
  brand?: CreditCardInfo['brand'];
  isNew?: boolean;
}

interface CreditCardContentProps {
  patient: Patient;
  appointmentId: string | undefined;
  selectedCardId: string;
  handleCardSelected: (newVal: string | undefined) => void;
  error?: string;
}

const labelForCard = (card: CreditCardInfo): string => {
  const formattedBrand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
  return `${formattedBrand} •••• ${card.lastFour}${card.default ? ' (Primary)' : ''}`;
};

const NEW_CARD = { id: 'new', label: 'Add new card' };

const CreditCardContent: FC<CreditCardContentProps> = (props) => {
  const { patient, appointmentId, selectedCardId, handleCardSelected, error } = props;
  const [cards, setCards] = useState<CreditCardInfo[]>([]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [warningMessage, setWarningMessage] = useState<string | undefined>(undefined);

  const {
    data: setupData,
    isFetching: isSetupDataFetching,
    isLoading: isSetupDataLoading,
    refetch: refetchSetupData,
    isRefetching: isSetupDataRefetching,
  } = useSetupStripe(patient?.id, appointmentId);

  useEffect(() => {
    if (setupData?.createdWithoutEmail) {
      setWarningMessage(
        'Responsible party email address is invalid. The Stripe customer was created/updated without an email — the patient will not receive email notifications from Stripe.'
      );
    } else {
      setWarningMessage(undefined);
    }
  }, [patient?.id, setupData?.createdWithoutEmail]);

  const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_KEY, setupData?.stripeAccount);

  const { mutate: setDefault } = useSetDefaultPaymentMethod(patient?.id, appointmentId);

  const {
    isFetching: cardsAreLoading,
    isFetched: cardsFetched,
    refetch: refetchPaymentMethods,
  } = useGetPaymentMethods({
    beneficiaryPatientId: patient?.id,
    appointmentId,
    setupCompleted: Boolean(setupData),
    onSuccess: (data) => {
      if (!data) return;

      setCards(data.cards ?? []);
      const defaultCard = data.cards.find((card) => card.default);
      if (defaultCard && !selectedCardId) {
        handleCardSelected(defaultCard.id);
      }
      void refetchSetupData();
    },
  });

  const showNewCard = (() => {
    const hasNone = cardsFetched && !cardsAreLoading && cards.length === 0;
    const addingOne = selectedCardId === NEW_CARD.id;
    return hasNone || addingOne;
  })();

  const initializing = isSetupDataLoading && !isSetupDataRefetching;

  const cardOptions: CardOption[] = [
    ...cards.map((card) => ({ id: card.id, label: labelForCard(card), brand: card.brand })),
    { id: NEW_CARD.id, label: NEW_CARD.label, isNew: true },
  ];

  const selectedCard = cardOptions.find((card) => card.id === selectedCardId);
  const someDefault = cards.some((card) => card.default);

  const handleNewPaymentMethod = async (id: string, makeDefault: boolean): Promise<void> => {
    if (makeDefault) {
      setDefault({
        paymentMethodId: id,
        onSuccess: async () => {
          await refetchPaymentMethods();
        },
        onError: (error) => {
          console.error('setDefault error', error);
          setErrorMessage('Unable to set default payment method. Please try again later or select a card.');
        },
      });
    } else {
      await refetchPaymentMethods();
    }
    handleCardSelected(id);
  };

  if (initializing) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  const currentValue = selectedCard;
  const showCardList = cards.length > 0;
  return (
    <>
      <Autocomplete
        size="small"
        aria-label="Default card selection radio group"
        fullWidth
        sx={{
          '.MuiFormControlLabel-label': {
            width: '100%',
          },
          gap: 1,
          display: showCardList ? 'initial' : 'none',
          marginBottom: 2,
        }}
        options={cardOptions}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.brand && (
              <Box sx={{ mr: 1, display: 'inline-flex', alignItems: 'center' }}>
                <CreditCardBrandIcon brand={option.brand} />
              </Box>
            )}
            {option.label}
          </li>
        )}
        value={currentValue ?? null}
        renderInput={(params: AutocompleteRenderInputParams) => {
          return (
            <Box>
              <TextField
                {...params}
                fullWidth
                required
                label="Credit card"
                variant="outlined"
                error={Boolean(error)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: currentValue?.brand ? (
                    <>
                      <Box sx={{ mr: 1, display: 'inline-flex', alignItems: 'center' }}>
                        <CreditCardBrandIcon brand={currentValue.brand} />
                      </Box>
                      {params.InputProps.startAdornment}
                    </>
                  ) : (
                    params.InputProps.startAdornment
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off',
                }}
              />
              {error && <FormHelperText error={Boolean(error)}>{error}</FormHelperText>}
            </Box>
          );
        }}
        onChange={(_event, value) => {
          handleCardSelected(value?.id);
        }}
      />

      <Elements stripe={stripePromise} options={{ clientSecret: setupData?.clientSecret }}>
        <Box
          sx={{
            width: '100%',
            display: showNewCard ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexDirection: 'column',
            marginTop: 2,
          }}
        >
          <AddCreditCardForm
            clientSecret={setupData?.clientSecret ?? ''}
            disabled={false}
            selectPaymentMethod={(id) => {
              void handleNewPaymentMethod(id, !someDefault);
            }}
            condition="I have obtained the consent to add a card on file from the patient"
            showAddButton={true}
          />
          {error && !showCardList && <FormHelperText error={Boolean(error)}>{error}</FormHelperText>}
        </Box>
      </Elements>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={errorMessage !== undefined}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(undefined)}
      >
        <Alert onClose={() => setErrorMessage(undefined)} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={warningMessage !== undefined}
        autoHideDuration={8000}
        onClose={() => setWarningMessage(undefined)}
      >
        <Alert onClose={() => setWarningMessage(undefined)} severity="warning" variant="filled">
          {warningMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreditCardContent;
